/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import QRCode from 'qrcode.react';
import { Node } from '@msdyn365-commerce-modules/utilities';
import { PaymentLink } from '@msdyn365-commerce/retail-proxy';

interface IPaymentLinkWithExpiration extends PaymentLink {
    ExpirationDateTime?: Date;
    PaymentLinkStatusValue?: number | undefined;
    PaymentLinkStatus?: string | number;
}

export interface IAsynchronousPaymentSummaryProps {
    containerClassName?: string;
    /** Pass PaymentLink[] directly since we show PaymentLink fields */
    paymentLinks?: PaymentLink[] | null;
    shouldShowQrCode?: boolean;
    cancelButton?: React.ReactNode;
    resources: {
        status: string;
        paymentLinkLabel: string;
        payActionLabel: string;
        asynchronousPaymentLabel: string;
    };
}

const statusLabel = (s: unknown) => {
    switch (s) {
        case 'None':
        case 0:
            return 'None';
        case 'Active':
        case 1:
            return 'Active';
        case 'Paid':
        case 2:
            return 'Paid';
        case 'Pending':
        case 3:
            return 'Pending';
        case 'Cancelled':
        case 4:
            return 'Cancelled';
        case 'Expired':
        case 5:
            return 'Expired';
        default:
            return String(s ?? '-');
    }
};

const AsynchronousPaymentSummary: React.FC<IAsynchronousPaymentSummaryProps> = ({
    paymentLinks,
    containerClassName,
    shouldShowQrCode,
    cancelButton,
    resources
}) => {
    const list = paymentLinks ?? [];
    const cssClassName = containerClassName ?? 'ms-create-asynchronous-payment__summary';
    return (
        <Node className={cssClassName}>
            {list.length === 0 ? (
                <div></div>
            ) : (
                <div className={`${cssClassName}-table`} role='table' aria-label='Asynchronous payments'>
                    <div className={`${cssClassName}-row header`} role='row'>
                        <div role='columnheader'>{resources.status}</div>
                        <div role='columnheader'>{resources.paymentLinkLabel}</div>
                    </div>
                    {list.map((p, i) => {
                        const linkId = p.PaymentLinkId;
                        // Prefer PaymentLinkStatusValue if present; fall back to PaymentLinkStatus
                        const linkStatus =
                            (p as IPaymentLinkWithExpiration).PaymentLinkStatusValue ?? (p as IPaymentLinkWithExpiration).PaymentLinkStatus;
                        const linkUrl = p.PaymentLinkUrl;

                        return (
                            <div key={linkId ?? i} className={`${cssClassName}-row`} role='row'>
                                <div role='cell'>{statusLabel(linkStatus)}</div>

                                <div role='cell' className={`${cssClassName}-url`}>
                                    {linkUrl ? (
                                        <>
                                            <a href={linkUrl} target='_blank' rel='noopener noreferrer' title={linkUrl}>
                                                {linkUrl}
                                            </a>
                                            {shouldShowQrCode ? (
                                                <div className={`${cssClassName}-qr-code`} aria-label='QR code for payment link'>
                                                    <QRCode value={linkUrl} size={120} />
                                                </div>
                                            ) : null}
                                            <div className={`${cssClassName}-pay-action-label`}>{resources.asynchronousPaymentLabel}</div>
                                            {cancelButton && <div className={`${cssClassName}-cancel-button`}>{cancelButton}</div>}
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Node>
    );
};

export default AsynchronousPaymentSummary;
